import {
  OidcProvider,
  useOidc,
  useOidcIdToken,
  useOidcAccessToken,
  useOidcUser,
} from '@axa-fr/react-oidc';
import React, { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useHttpClient } from '../../../hooks/http-hook';
import { checkImage } from '../../../util/helperfunctions';
import googleLogo from './Images/google-logo.svg';
import microsoftLogo from './Images/microsoft-logo.svg';
import oktaLogo from './Images/okta-logo.svg';

function Button({ configurationName, disabled, onLogin }) {
  const { t } = useTranslation();
  const { login } = useOidc(configurationName);

  return (
    <button
      type="button"
      className="grey_theme"
      onClick={() => {
        onLogin?.();
        login();
      }}
      id={configurationName}
      disabled={disabled}
    >
      {configurationName.toLowerCase() === 'microsoft' && (
        <img src={microsoftLogo} className="icon signup" alt="Microsoft Logo" />
      )}
      {configurationName.toLowerCase() === 'google' && (
        <img src={googleLogo} className="icon signup" alt="Google Logo" />
      )}
      {configurationName.toLowerCase() === 'okta' && (
        <img src={oktaLogo} className="icon signup" alt="Okta Logo" />
      )}
      {t('R051', { identityProvider: configurationName })}
    </button>
  );
}

function SignIn({
  configurationName,
  clientId,
  handleResponse,
  disabled,
  setErrorMessage,
  setShowError,
  setCompLoading,
}) {
  const { accessToken } = useOidcAccessToken(configurationName);
  const { idTokenPayload } = useOidcIdToken(configurationName);
  const { oidcUser } = useOidcUser(configurationName);
  const { isLoading, error, sendRequest } = useHttpClient();
  const [loginStarted, setLoginStarted] = useState(
    sessionStorage.getItem('loginStarted') === 'true'
  );

  useEffect(() => {
    if (error) {
      setShowError?.(true);
      setErrorMessage(error?.message || String(error));
    }
  }, [error, setErrorMessage, setShowError]);

  const fetchMicrosoftImage = useCallback(
    async (response, picture, token) => {
      const headers = new Headers();
      headers.append('Authorization', `Bearer ${token}`);

      const options = {
        method: 'GET',
        headers,
      };

      // Fetch Microsoft profile picture
      const photoBlob = await fetch(picture, options)
        .then((response) => response.blob())
        .catch((error) => console.log(error));

      const validImage = await checkImage(photoBlob);

      if (validImage) {
        const fileName = response.user?._id ?? response.userId;

        // Convert the blob to a File object
        const profileImageFile = new File([photoBlob], fileName, {
          type: photoBlob.type,
        });

        // Get Signed URL for AWS S3 upload
        const presignedUrl = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/users/getSignedUrl/${
            response.user?._id ?? response.userId
          }?filename=${encodeURIComponent(fileName)}`,
          'PUT'
        );

        if (presignedUrl) {
          // Upload image to AWS S3
          await sendRequest(presignedUrl.toString(), 'PUT', profileImageFile, {
            'Content-Type': 'image/jpeg',
          });

          const url = presignedUrl.split('?')[0];

          // Pass response and profile picture to ensure it saves on user's profile creation
          if (handleResponse) {
            // Remove all saved data from sessionStorage to ensure user has to re-authenticate
            sessionStorage.clear();
            handleResponse({ ...response, profilePic: url });
          }
        }
      } else {
        // If image is invalid, pass response without profile picture
        if (handleResponse) {
          // Remove all saved data from sessionStorage to ensure user has to re-authenticate
          sessionStorage.clear();
          handleResponse(response);
        }
      }
    },
    [handleResponse, sendRequest]
  );

  const validateAuthentication = useCallback(
    async (name, email, picture, token, expiryDate) => {
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/users/signInSSO`,
          'POST',
          JSON.stringify({
            name,
            email,
            picture,
            token,
            expiryDate,
            clientId,
            auth: configurationName,
            platform: 'web',
          })
        );

        if (
          configurationName.toLowerCase() === 'microsoft' &&
          !response.user?.completed_registration
        ) {
          fetchMicrosoftImage(response, picture, token);
        } else if (handleResponse) {
          // Remove all saved data from sessionStorage to ensure user has to re-authenticate
          sessionStorage.clear();
          handleResponse(response);
        }
      } catch (err) {
        setShowError?.(true);
        setErrorMessage(err instanceof Error ? err.message : String(err));
      } finally {
        setCompLoading(false);
      }
    },
    [
      clientId,
      configurationName,
      fetchMicrosoftImage,
      handleResponse,
      sendRequest,
      setShowError,
      setErrorMessage,
      setCompLoading,
    ]
  );

  useEffect(() => {
    if (loginStarted && accessToken && idTokenPayload && oidcUser) {
      validateAuthentication(
        oidcUser.given_name ?? oidcUser.name,
        oidcUser.email ?? oidcUser.preferred_username,
        oidcUser.picture,
        accessToken,
        idTokenPayload.exp
      );
      sessionStorage.setItem('loginStarted', 'false');
    }
  }, [accessToken, idTokenPayload, oidcUser, validateAuthentication, loginStarted]);

  return (
    <Button
      configurationName={configurationName}
      disabled={disabled}
      onLogin={() => {
        sessionStorage.setItem('loginStarted', 'true');
      }}
    />
  );
}

function OpenIDAuthButton(props) {
  let configuration = {
    client_id: props.ssoId,
    redirect_uri: props.redirectUri,
    scope: props.scope,
    authority: props.authority,
    response_type: 'code',
  };

  if (props.secret) {
    let extras = {};

    if (props.configurationName.toLowerCase() === 'microsoft') {
      extras = {
        clientSecret: props.secret,
      };
    } else {
      extras = {
        client_secret: props.secret,
      };
    }

    configuration = {
      ...configuration,
      token_request_extras: extras,
    };
  }

  return (
    <OidcProvider
      configuration={configuration}
      configurationName={props.configurationName}
      loadingComponent={() => <Button {...props} disabled />}
      authenticatingComponent={() => <Button {...props} disabled />}
      callbackSuccessComponent={() => <Button {...props} disabled />}
      sessionLostComponent={() => <Button {...props} />}
      authenticatingErrorComponent={() => <Button {...props} />}
    >
      <SignIn {...props} />
    </OidcProvider>
  );
}

export default OpenIDAuthButton;
